import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry } from 'rxjs';

import { ApiResponseDTO } from '../http/definitions';
import { environment } from '../environment/environment';
import {
  LogoutDTO,
  RefreshTokenDTO,
  TokenResponseDTO,
  UpdateProfileDTO,
  UserDTO,
  UserSessionDTO,
} from '../types/auth.types';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly baseUrl = environment.authApiUrl;
  private readonly defaultTimeout = 10000; // 10 seconds
  private readonly retryAttempts = 2;

  constructor(private http: HttpClient) {}

  /**
   * Refresh access token using refresh token
   * @param data - RefreshTokenDTO
   * @returns Observable<ApiResponseDTO<TokenResponseDTO>>
   */
  refreshToken(
    data: RefreshTokenDTO
  ): Observable<ApiResponseDTO<TokenResponseDTO>> {
    return this.http
      .post<ApiResponseDTO<TokenResponseDTO>>(
        `${this.baseUrl}/auth/refresh`,
        { refresh_token: data.refresh_token },
        { headers: this.getHeaders() }
      )
      .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
  }

  /**
   * Logout
   * @param data - LogoutDTO
   * @returns Observable<ApiResponse<null>>
   */
  logout(data: LogoutDTO): Observable<ApiResponseDTO<null>> {
    return this.http
      .post<ApiResponseDTO<null>>(`${this.baseUrl}/auth/logout`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(timeout(this.defaultTimeout), catchError(this.handleError));
  }

  /**
   * Get profile
   * @returns Observable<ApiResponseDTO<UserDTO>>
   */
  getProfile(): Observable<ApiResponseDTO<UserDTO>> {
    return this.http
      .get<ApiResponseDTO<UserDTO>>(`${this.baseUrl}/users/profile`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Update profile
   * @param data - UpdateProfileDTO
   * @returns Observable<ApiResponseDTO<null>>
   */
  updateProfile(data: UpdateProfileDTO): Observable<ApiResponseDTO<null>> {
    return this.http
      .put<ApiResponseDTO<null>>(`${this.baseUrl}/users/profile`, data, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get user sessions
   * @returns Observable<ApiResponseDTO<UserSessionDTO[]>>
   */
  getUserSessions(): Observable<ApiResponseDTO<UserSessionDTO[]>> {
    return this.http
      .get<ApiResponseDTO<UserSessionDTO[]>>(`${this.baseUrl}/user/session`, {
        headers: this.getAuthHeaders(true),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Invalidate session
   * @param sessionId - string
   * @returns Observable<ApiResponseDTO<null>>
   */
  invalidateSession(sessionId: string): Observable<ApiResponseDTO<null>> {
    return this.http
      .delete<ApiResponseDTO<null>>(
        `${this.baseUrl}/user/session/${sessionId}`,
        {
          headers: this.getAuthHeaders(),
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(this.handleError)
      );
  }

  /**
   * Get default headers for API requests
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }

  /**
   * Get headers with authorization token for protected routes
   */
  private getAuthHeaders(includeSessionId = false): HttpHeaders {
    const token = localStorage.getItem('access_token');
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    });

    // Add session ID header if requested and available
    if (includeSessionId) {
      const sessionId = localStorage.getItem('session_id');
      if (sessionId) {
        headers = headers.set('X-Session-ID', sessionId);
      }
    }

    return headers;
  }

  /**
   * Handle HTTP errors
   */
  private handleError = (error: any): Observable<never> => {
    let errorMessage = 'An unexpected error occurred';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage =
        'Unable to connect to server. Please check your internet connection.';
    } else if (error.status >= 400 && error.status < 500) {
      errorMessage =
        error.error?.message || 'Invalid request. Please check your input.';
    } else if (error.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    console.error('API Error:', error);
    return throwError(() => new Error(errorMessage));
  };
}
